import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Button,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { CtyButton, CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import {
  useCreatePronunciationDictionaryMutation,
  type PronunciationDictionaryEntry,
} from '@circuitry-ai/doc-data';

interface CreateDictionaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (dictionaryName: string) => void;
}

type EntryRow = PronunciationDictionaryEntry & { localId: string };

const emptyEntry = (): EntryRow => ({
  localId: crypto.randomUUID(),
  text: '',
  pronunciation: '',
});

const CreateDictionaryModal: React.FC<CreateDictionaryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [entries, setEntries] = useState<EntryRow[]>([emptyEntry()]);

  const [createDictionary, { isLoading }] = useCreatePronunciationDictionaryMutation();

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setEntries([emptyEntry()]);
  }, [isOpen]);

  const handleEntryChange = useCallback(
    (localId: string, field: keyof PronunciationDictionaryEntry, value: string) => {
      setEntries((prev) =>
        prev.map((entry) => (entry.localId === localId ? { ...entry, [field]: value } : entry))
      );
    },
    []
  );

  const handleAddEntryRow = useCallback(() => {
    setEntries((prev) => [...prev, emptyEntry()]);
  }, []);

  const handleRemoveEntryRow = useCallback((localId: string) => {
    setEntries((prev) =>
      prev.length <= 1 ? prev : prev.filter((entry) => entry.localId !== localId)
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    const dictionaryName = name.trim();
    if (!dictionaryName) {
      CtyShowToast(checkType.error, t('Dictionary name is required.'));
      return;
    }

    const items = entries
      .map((entry) => ({
        text: entry.text.trim(),
        pronunciation: entry.pronunciation.trim(),
      }))
      .filter((entry) => entry.text && entry.pronunciation);

    try {
      await createDictionary({ name: dictionaryName, items }).unwrap();
      CtyShowToast(checkType.success, t('Dictionary created successfully.'));
      onSuccess(dictionaryName);
      onClose();
    } catch (err) {
      CtyShowToast(
        checkType.error,
        (err as { data?: { message?: string } })?.data?.message ||
          (err as Error)?.message ||
          t('Failed to create dictionary.')
      );
    }
  }, [createDictionary, entries, name, onClose, onSuccess, t]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader>{t('New dictionary')}</ModalHeader>
        <ModalBody className="gap-4">
          <Input
            label={t('Dictionary name')}
            placeholder={t('e.g. Brand & vendor names')}
            value={name}
            onValueChange={setName}
            variant="bordered"
            isRequired
          />
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-foreground">{t('Initial entries')}</p>
            {entries.map((entry, index) => (
              <div
                key={entry.localId}
                className="flex flex-col gap-2 p-3 border border-divider rounded-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-default-500">
                    {t('Entry')} {index + 1}
                  </span>
                  {entries.length > 1 && (
                    <Button
                      isIconOnly
                      size="sm"
                      variant="light"
                      color="danger"
                      aria-label={t('Remove entry')}
                      onPress={() => handleRemoveEntryRow(entry.localId)}
                    >
                      <Icon icon="mdi:close" className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <Input
                  label={t('Word')}
                  placeholder={t('Word or phrase')}
                  value={entry.text}
                  onValueChange={(v) => handleEntryChange(entry.localId, 'text', v)}
                  variant="bordered"
                />
                <Input
                  label={t('Pronunciation')}
                  placeholder={t('IPA or phonetic spelling')}
                  value={entry.pronunciation}
                  onValueChange={(v) => handleEntryChange(entry.localId, 'pronunciation', v)}
                  variant="bordered"
                />
              </div>
            ))}
            <Button
              size="sm"
              variant="bordered"
              onPress={handleAddEntryRow}
              startContent={<Icon icon="mdi:plus" className="w-4 h-4" />}
            >
              {t('Add entry')}
            </Button>
          </div>
        </ModalBody>
        <ModalFooter>
          <CtyButton variant="light" onPress={onClose} isDisabled={isLoading}>
            {t('Cancel')}
          </CtyButton>
          <CtyButton color="primary" onPress={() => void handleSubmit()} isLoading={isLoading}>
            {t('Create dictionary')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateDictionaryModal;
